// Elo ratings for the Australian codes.
//
// Every team starts on 1500 and trades points with whoever it plays: beat a
// side the ratings said you should lose to and you take a lot, beat the side
// you were meant to beat and you take almost nothing. Two departures from
// chess Elo make it work on football:
//
//   Margin. A 60-point win says more than a 2-point win, so the update is
//   scaled by ln(|margin| + 1). Left there, a dominant side would run away
//   forever, so the multiplier is damped by how far ahead of its opponent the
//   favourite already was — thrashing a bottom side moves you less than
//   thrashing a top one.
//
//   Off-season. Lists change, ratings should not carry over whole. At every
//   season boundary each rating is pulled a quarter of the way back to the
//   competition mean, which is the regression a year of drafting and delisting
//   roughly works out to.
//
// The state is plain JSON — a ratings dictionary and the params that made it —
// so it can be published to storage and read by a phone that has never seen a
// historical match.

import { logLoss } from '../metrics.js';
import { closeProbability } from './closeLine.js';
import { assertBefore } from './base.js';

export const ELO_SPORTS = ['afl', 'nrl', 'nbl', 'bbl'];

/**
 * WHAT THE RATINGS ARE CHASING (F2).
 *
 * `result` is what Elo has always done: a win is a 1, a loss is a 0, and the
 * ratings move by the surprise. On that label Elo loses to a closing line, and
 * the encompassing gate prints nothing — which is a loss-function problem
 * rather than a data one (Hubáček & Šír 2023; see `models/closeLine.js`).
 *
 * `close` swaps the label for the market's own final answer: the devigged
 * closing probability of the home side. The ratings then chase where the price
 * ended up rather than who happened to win, which is a far less noisy target —
 * one game's result is a single Bernoulli draw, the close is the market's
 * estimate of the same quantity. A match with no closing price to read falls
 * back to the result, so the option never silently drops half a season.
 *
 * The DEFAULT STAYS `result`. This is an option to be measured, not a change of
 * mind: whether it helps is a question for the goldens.
 */
export const ELO_TARGETS = ['result', 'close'];

const DEFAULT_PARAMS = {
  k: 30,
  hga: 35,          // rating points of home-ground advantage
  regress: 0.75,    // fraction of a rating carried across a season boundary
  base: 1500,
  movScale: 2.2,    // numerator of the margin-of-victory damping
  movDiv: 0.001,    // how fast the damping bites as the rating gap grows
  marginPerRating: null, // reserved: rating points per point of expected margin
  target: 'result',      // or 'close' — see ELO_TARGETS
};

const DEFAULT_GRID = { k: [20, 30, 40], hga: [20, 35, 50], regress: [0.6, 0.75, 0.9] };

/** Expected home score (1 = win) from the rating gap, home advantage included. */
export function expectedHome(ratingHome, ratingAway, hga) {
  return 1 / (1 + Math.pow(10, -((ratingHome + hga - ratingAway) / 400)));
}

/**
 * How much one result is allowed to move the ratings. Grows with the margin,
 * shrinks as the winner's rating advantage grows — the second factor is what
 * stops a good side inflating itself on the same beatable opponents.
 */
export function movMultiplier(margin, ratingGap, { movScale, movDiv }) {
  return (Math.log(Math.abs(margin) + 1) * movScale) / (movDiv * Math.abs(ratingGap) + movScale);
}

const seasonOf = (match) => String(match?.season ?? new Date(match?.kickoff).getUTCFullYear());

/** Only a finished match with a winner teaches anything. */
function isPlayed(match) {
  return !!match?.result?.winner && match.result.status !== 'scheduled';
}

function score(winner) {
  if (winner === 'home') return 1;
  if (winner === 'draw') return 0.5;
  return 0;
}

/**
 * The number this match teaches, under whichever target is set: the result as a
 * 0, a half or a 1, or the devigged closing probability of the home side. A
 * `close` target with no closing price on the match falls back to the result
 * rather than dropping the game.
 */
function actualFor(match, target) {
  if (target === 'close') {
    const p = closeProbability(match, 'home');
    if (Number.isFinite(p) && p > 0 && p < 1) return p;
  }
  return score(match.result.winner);
}

/** Pull every rating toward the mean — the off-season list churn, in one line. */
function regressToMean(ratings, regress, base) {
  const ids = Object.keys(ratings);
  if (ids.length === 0) return ratings;
  const mean = ids.reduce((sum, id) => sum + ratings[id], 0) / ids.length || base;
  const out = {};
  for (const id of ids) out[id] = mean + regress * (ratings[id] - mean);
  return out;
}

/** One match applied to a ratings dictionary, in place. Returns the rating gap used. */
function applyMatch(ratings, match, params, hga) {
  const { base, k } = params;
  const homeId = match.home?.id;
  const awayId = match.away?.id;
  const rh = ratings[homeId] ?? base;
  const ra = ratings[awayId] ?? base;
  const gap = rh + hga - ra;
  const expected = expectedHome(rh, ra, hga);
  const actual = actualFor(match, params.target);

  // A result with no scores still tells us who won; treat it as a par-margin
  // game rather than throwing the match away.
  const margin = Number.isFinite(match.result.homeScore) && Number.isFinite(match.result.awayScore)
    ? match.result.homeScore - match.result.awayScore
    : null;
  const mov = margin === null ? 1 : movMultiplier(margin, gap, params);

  const delta = k * mov * (actual - expected);
  ratings[homeId] = rh + delta;
  ratings[awayId] = ra - delta;
  return gap;
}

const byKickoff = (a, b) => String(a.kickoff).localeCompare(String(b.kickoff));

export const elo = {
  id: 'elo',
  version: 1,
  sports: ELO_SPORTS,
  needsFit: true,
  defaultParams: { ...DEFAULT_PARAMS },

  /**
   * Walk the history in kickoff order and return the ratings it leaves behind.
   * `asOf` is the moment the state is claimed to be true at; nothing that
   * starts at or after it is allowed to have been seen.
   */
  fit(history, asOf, params = {}, { strict = false } = {}) {
    const p = { ...DEFAULT_PARAMS, ...params };
    const rows = assertBefore(history, asOf, strict).filter(isPlayed).slice().sort(byKickoff);

    let ratings = {};
    const hga = {};
    let season = null;
    let games = 0;
    for (const match of rows) {
      const s = seasonOf(match);
      if (season !== null && s !== season) ratings = regressToMean(ratings, p.regress, p.base);
      season = s;
      hga[match.sport] ??= p.hga;
      applyMatch(ratings, match, p, hga[match.sport]);
      games += 1;
    }

    return { ratings, hga, params: p, fittedTo: new Date(asOf).toISOString(), season, games };
  },

  predict(state, match, selection) {
    if (selection !== 'home' && selection !== 'away') return null;
    const p = { ...DEFAULT_PARAMS, ...(state?.params ?? {}) };
    const ratings = state?.ratings ?? {};
    const rh = ratings[match.home?.id] ?? p.base;
    const ra = ratings[match.away?.id] ?? p.base;
    const hga = state?.hga?.[match.sport] ?? p.hga;
    const pHome = expectedHome(rh, ra, hga);
    return selection === 'home' ? pHome : 1 - pHome;
  },

  /**
   * One more match into an existing state — what the nightly job calls when a
   * result lands, so it never has to refit five seasons to learn one game.
   * Identical arithmetic to a single step of `fit`, season boundary included.
   */
  update(state, match) {
    if (!isPlayed(match)) return state;
    const p = { ...DEFAULT_PARAMS, ...(state?.params ?? {}) };
    const s = seasonOf(match);
    let ratings = { ...(state?.ratings ?? {}) };
    if (state?.season != null && s !== state.season) ratings = regressToMean(ratings, p.regress, p.base);
    const hga = { ...(state?.hga ?? {}) };
    hga[match.sport] ??= p.hga;
    applyMatch(ratings, match, p, hga[match.sport]);
    return {
      ratings,
      hga,
      params: p,
      fittedTo: new Date(match.kickoff).toISOString(),
      season: s,
      games: (state?.games ?? 0) + 1,
    };
  },

  /**
   * Walk-forward parameter search. For each combination the ratings are built
   * from every completed season and the next season is scored cold — the
   * season under test never touches the ratings that predict it, which is the
   * only honest way to choose k.
   *
   * One pass per combination, so the cost is grid x matches.
   */
  tune(history, asOf, grid = DEFAULT_GRID, { strict = false, target = DEFAULT_PARAMS.target } = {}) {
    const rows = assertBefore(history, asOf, strict).filter(isPlayed).slice().sort(byKickoff);
    const seasons = [];
    for (const match of rows) {
      const s = seasonOf(match);
      if (!seasons.length || seasons[seasons.length - 1].season !== s) seasons.push({ season: s, matches: [] });
      seasons[seasons.length - 1].matches.push(match);
    }

    const combos = [];
    for (const k of grid.k ?? DEFAULT_GRID.k) {
      for (const hgaValue of grid.hga ?? DEFAULT_GRID.hga) {
        for (const regress of grid.regress ?? DEFAULT_GRID.regress) combos.push({ k, hga: hgaValue, regress });
      }
    }

    // WHAT A COMBINATION IS SCORED AGAINST. Under `result` it is the outcome, a
    // 0 or a 1, and draws are left out because Elo has no third answer. Under
    // `close` it is the market's own devigged closing probability — a soft
    // label, which `logLoss` takes as the cross-entropy it already is — and a
    // draw is no longer a special case, because the close prices one.
    const labelOf = (match) => {
      if (target !== 'close') {
        return match.result.winner === 'draw' ? null : (match.result.winner === 'home' ? 1 : 0);
      }
      const p = closeProbability(match, 'home');
      return Number.isFinite(p) && p > 0 && p < 1 ? p : null;
    };

    let best = null;
    for (const combo of combos) {
      const p = { ...DEFAULT_PARAMS, ...combo, target };
      let ratings = {};
      const hga = {};
      const perSeason = [];
      seasons.forEach((entry, i) => {
        if (i > 0) {
          ratings = regressToMean(ratings, p.regress, p.base);
          const pairs = [];
          for (const match of entry.matches) {
            const y = labelOf(match);
            if (y === null) continue;
            const state = { ratings, hga, params: p };
            const pHome = elo.predict(state, match, 'home');
            if (pHome === null) continue;
            pairs.push({ p: pHome, y });
          }
          if (pairs.length) perSeason.push(logLoss(pairs));
        }
        for (const match of entry.matches) {
          hga[match.sport] ??= p.hga;
          applyMatch(ratings, match, p, hga[match.sport]);
        }
      });
      if (!perSeason.length) continue;
      const mean = perSeason.reduce((sum, x) => sum + x, 0) / perSeason.length;
      if (!best || mean < best.logLoss) best = { params: p, logLoss: mean };
    }

    return best
      ? { params: best.params, logLoss: best.logLoss, tried: combos.length, target }
      : { params: { ...DEFAULT_PARAMS, target }, logLoss: null, tried: combos.length, target };
  },
};
